import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, OnDestroy, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { ResidentCarPersonalAccountFormDto } from '@zaoblako/shared/modules/cctv/dtos/resident-car/personal-account/resident-car-personal-account-form.dto';
import { ConfirmDialogService } from '@zaoblako/web/core/services/confirm-dialog.service';
import { ErrorService } from '@zaoblako/web/core/services/error.service';
import { PersonalAccountResidentCarService } from '@zaoblako/web/core/services/lk/personal-account-resident-car.service';
import { getCategoryFilterRoomIds, haveRoomIdsChanged } from '@zaoblako/web/modules/passage/utils/passage-form-room-category-filters.util';
import { FormType } from '@zaoblako/web/shared/components/form-sidebar/models/form-state';
import { FormStoreService } from '@zaoblako/web/shared/components/form-sidebar/services/form-store.service';
import { AuthService } from '@zaoblako/web/core/services/security/auth.service';
import { BaseFormComponent } from '@zaoblako/web/shared/dumbs/base-form/base-form.component';
import { PersonalAccountCarCategorySingleSelectComponent } from '@zaoblako/web/shared/dumbs/dropdowns/personal-account-car-category-single-select/personal-account-car-category-single-select.component';
import { PersonalAccountRoomMultiSelectComponent } from '@zaoblako/web/shared/dumbs/dropdowns/personal-account-room-multi-select/personal-account-room-multi-select.component';
import { PersonalAccountExternalUserSingleSelectComponent } from '@zaoblako/web/shared/dumbs/dropdowns/personal-account-external-user-single-select/personal-account-external-user-single-select.component';
import { InputTextComponent } from '@zaoblako/web/shared/dumbs/input-text/input-text.component';
import { SpinnerComponent } from '@zaoblako/web/shared/dumbs/spinner/spinner.component';
import { JoinPipe } from '@zaoblako/web/shared/pipes/join.pipe';
import { ButtonComponent } from '@zaoblako/web/shared/dumbs/button/button.component';
import type { ResidentCarPersonalAccountFindInterface } from '@zaoblako/shared/modules/cctv/dtos/resident-car/personal-account/resident-car-personal-account-find.dto';
import type { RoomFindInterface } from '@zaoblako/shared/modules/catalog/dtos/room/room-find.dto';
import type { ResidentCarPersonalAccountFormInterface } from '@zaoblako/shared/modules/cctv/dtos/resident-car/personal-account/resident-car-personal-account-form.dto';

import { CardModule } from 'primeng/card';
import { Observable, Subscription } from 'rxjs';
import { MessagesModule } from 'primeng/messages';
import type { ExternalUserFindInterface } from '@zaoblako/shared/modules/security/dtos/external-user/external-user-find.dto';

@Component({
  selector: 'crm-lk-eu-resident-car-form',
  host: { class: 'frm-filters-ui-v2' },
  standalone: true,
  imports: [
    CommonModule,
    CardModule,
    SpinnerComponent,
    InputTextComponent,
    JoinPipe,
    PersonalAccountExternalUserSingleSelectComponent,
    PersonalAccountCarCategorySingleSelectComponent,
    PersonalAccountRoomMultiSelectComponent,
    ButtonComponent,
    MessagesModule
  ],
  templateUrl: './lk-eu-resident-car-form.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class LkEuResidentCarFormComponent
  extends BaseFormComponent<ResidentCarPersonalAccountFormInterface>
  implements OnInit, OnDestroy {
  @Input() public readonly index: number;
  public car = <ResidentCarPersonalAccountFormInterface>{};
  public dto = ResidentCarPersonalAccountFormDto;
  private roomsBeforeLastChange?: RoomFindInterface[];

  public form$: Observable<FormType>;
  public loading$: Observable<boolean>;

  private formSubscription$: Subscription = new Subscription();

  public constructor(
    private readonly formStoreService: FormStoreService,
    private readonly personalAccountResidentCarService: PersonalAccountResidentCarService,
    private readonly confirmService: ConfirmDialogService,
    private readonly errorService: ErrorService,
    private readonly authService: AuthService,
    private readonly router: Router,
    private readonly cdRef: ChangeDetectorRef
  ) {
    super();
  }

  public ngOnInit(): void {
    this.form$ = this.formStoreService.select((state) => state.forms[this.index]);
    this.loading$ = this.formStoreService.select((state) => state.forms[this.index]?.loading);

    if (this.formStoreService.getState.forms[this.index]?.isEdit) {
      this.getById();
    } else {
      if (this.authService?.currentUser?.id && !this.car.owner) {
        this.car.owner = this.authService.currentUser as unknown as ExternalUserFindInterface;
      }
    }
  }

  public get categoryFilterRoomIds(): number[] | null | undefined {
    return getCategoryFilterRoomIds(this.car.rooms);
  }

  public onRoomsChange(rooms: RoomFindInterface[]): void {
    if (haveRoomIdsChanged(this.roomsBeforeLastChange, rooms)) {
      this.car.category = null;
    }
    this.roomsBeforeLastChange = rooms?.length ? [...rooms] : [];
    this.cdRef.markForCheck();
  }

  public ngOnDestroy(): void {
    this.formSubscription$?.unsubscribe();
  }

  public getById(): void {
    this.formStoreService.setLoading(true, this.index);

    this.personalAccountResidentCarService
      .findById<ResidentCarPersonalAccountFindInterface>(this.formStoreService.getState.forms[this.index]?.entityId)
      .subscribe({
        next: (car) => {
          this.car = car;
          this.roomsBeforeLastChange = this.car.rooms?.length ? [...this.car.rooms] : [];

          this.formStoreService.setLoading(false, this.index);
          this.cdRef.markForCheck();
        }
      });
  }

  public saveCar(): void {
    if (this.checkValidate(this.car).valid) {
      if (this.formStoreService.getState.forms[this.index]?.entityId) {
        this.formStoreService.setLoading(true, this.index);

        this.personalAccountResidentCarService
          .update<ResidentCarPersonalAccountFormInterface, ResidentCarPersonalAccountFindInterface>(this.car)
          .subscribe({
            next: (car) => {
              this.formStoreService.setLoading(false, this.index);
              this.errorService.addSuccessMessage(`Постоянное ТС "${car?.carNumber}" обновлено!`);
              this.formStoreService.saveNext(car, this.index);
            },
            error: () => {
              this.formStoreService.setLoading(false, this.index);
            }
          });
      } else {
        this.formStoreService.setLoading(true, this.index);

        this.personalAccountResidentCarService.create<ResidentCarPersonalAccountFindInterface>(this.car).subscribe({
          next: (car) => {
            this.formStoreService.setLoading(false, this.index);
            this.errorService.addSuccessMessage('Постоянное ТС создано!');
            if (this.formStoreService.getState.forms[this.index]?.isRedirectInCreate) {
              this.formStoreService.closeSidebar(this.index);
              this.router.navigate(['/lk/external-user/resident-car/card/' + car.id]).catch(console.error);
            } else {
              this.formStoreService.saveNext(car, this.index);
            }
          },
          error: () => {
            this.formStoreService.setLoading(false, this.index);
          }
        });
      }
    }
  }

  public deleteCar() {
    this.confirmService.confirm({
      message: `Вы действительно хотите удалить постоянное ТС "${this.car?.carNumber}"?`,
      accept: () => {
        this.formStoreService.setLoading(true, this.index);
        this.cdRef.markForCheck();

        this.personalAccountResidentCarService
          .deleteById(this.formStoreService.getState.forms[this.index]?.entityId)
          .subscribe(() => {
            this.errorService.addSuccessMessage(`Постоянное ТС "${this.car?.carNumber}" удалено!`);
            this.formStoreService.deleteNext(this.index);
          });
      }
    });
  }

  public restoreCar() {
    this.confirmService.confirm({
      message: `Вы действительно хотите восстановить постоянное ТС "${this.car?.carNumber}"?`,
      accept: () => {
        this.formStoreService.setLoading(true, this.index);
        this.cdRef.markForCheck();

        this.personalAccountResidentCarService
          .restore(this.formStoreService.getState.forms[this.index]?.entityId)
          .subscribe({
            next: () => {
              this.car = { ...this.car, deleted: null } as ResidentCarPersonalAccountFindInterface;
              this.cdRef.markForCheck();
              this.errorService.addSuccessMessage(`Постоянное ТС "${this.car?.carNumber}" восстановлено!`);
              this.formStoreService.saveNext(this.car, this.index);
            },
            error: () => {
              this.formStoreService.setLoading(false, this.index);
            }
          });
      }
    });
  }
}
